import { useEffect, useRef, useState, type ReactNode } from "react";
import { playClip } from "./EvaPlayButton";

const COOLDOWN_MS = 6000;
let lastFiredAt = 0;

/**
 * EvaAvatarEgg — invisible click overlay that sits over Eva's avatar so the
 * avatar's original layout/sizing/border-ring is preserved exactly. Tapping
 * plays an "easter egg" clip and shows a tiny floating bubble below for ~1.6s.
 * - 6s cooldown prevents spam-tap looping (audio only).
 * - Bubble re-shows even during cooldown so the gesture always feels responsive.
 * - Reduced motion: bubble appears instantly with no float animation.
 */
type Props = {
  children?: ReactNode;
  /** override clip id — defaults to easter_eye */
  clipId?: string;
};

export const EvaAvatarEgg = ({ children, clipId = "easter_eye" }: Props) => {
  const [showBubble, setShowBubble] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  }, []);

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Always re-show the bubble — instant feedback for the gesture.
    setShowBubble(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setShowBubble(false), 1600);

    // Audio only fires outside the cooldown window.
    const now = Date.now();
    if (now - lastFiredAt < COOLDOWN_MS) return;
    lastFiredAt = now;
    playClip(clipId).catch((err) => console.warn("[EvaAvatarEgg]", err));
  };

  return (
    <>
      <button
        type="button"
        aria-label="Easter egg — tap Eva's eye"
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(e); }}
        className="eva-avatar-egg"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          background: "transparent",
          border: 0,
          padding: 0,
          margin: 0,
          cursor: "pointer",
          outline: "none",
          WebkitTapHighlightColor: "transparent",
          borderRadius: "inherit",
          zIndex: 2,
        }}
      />
      {showBubble && (
        <div
          role="status"
          aria-live="polite"
          className={reduceMotion ? "eva-egg-bubble" : "eva-egg-bubble eva-egg-bubble-float"}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(26,15,5,0.95)",
            color: "#FFFDF8",
            padding: "5px 9px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "Barlow, sans-serif",
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            zIndex: 50,
            pointerEvents: "none",
            boxShadow: "0 6px 16px -4px rgba(0,0,0,0.35)",
          }}
        >
          👁️ that's my eye!
          <style>{`
            @keyframes eva-egg-float {
              0%   { opacity: 0; transform: translate(-50%, -4px); }
              20%  { opacity: 1; transform: translate(-50%, 0); }
              80%  { opacity: 1; transform: translate(-50%, 0); }
              100% { opacity: 0; transform: translate(-50%, -2px); }
            }
            .eva-egg-bubble-float { animation: eva-egg-float 1.6s ease-out both; }
            .eva-avatar-egg:focus-visible {
              box-shadow: 0 0 0 2px rgba(196,130,58,0.55);
            }
            @media (prefers-reduced-motion: reduce) {
              .eva-egg-bubble-float { animation: none !important; opacity: 1 !important; }
            }
          `}</style>
        </div>
      )}
      {children /* kept for backward-compat in case caller passes children inside */}
    </>
  );
};

export default EvaAvatarEgg;